import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { NetworkStatus, useMutation } from '@apollo/client'
import {
  ActionIcon,
  Avatar,
  Menu,
  SegmentedControl,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import FilterMenu from 'src/components/Overlays/FiltersMenu/FilterMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_AUTOMATED_RULES,
  SET_AUTOMATED_RULE_STATUS,
  DELETE_AUTOMATED_RULE,
} from 'src/graphql/automatedRules.graphql'
import { GET_JOB_LISTINGS } from 'src/graphql/joblistings.graphql'
import {
  AutomatedRuleActionType,
  AutomatedRuleStatus,
} from 'src/graphql/types/automatedRules'
import IconChecklist from 'src/icons/IconChecklist'
import IconCopy from 'src/icons/IconCopy'
import IconSearch from 'src/icons/IconSearch'
import IconThreeDots from 'src/icons/IconThreeDots'
import IconTrash from 'src/icons/IconTrash'
import {
  buildFullName,
  formatDateAbsolute,
  formatDateTimeForTooltip,
  formatSnakeValue,
  formatStepType,
} from 'src/lib/formatters'

import PipelineView from './components/PipelineView/PipelineView'
import RuleDetailsPage from './RuleDetailsPage'
import {
  ACTION_ICON_CLASS,
  ACTION_ICON_MAP,
  ACTION_SORT_ORDER,
} from './utils/actionDisplay'
import {
  resolveRulesPageInfo,
  TABLE_PAGE_LIMIT,
} from './utils/pipelinePageInfo'
import {
  ACTIONS_KEYS,
  CONDITIONS_KEYS,
  STEP_CONDITION_KEY,
  formatTagConditionLabel,
} from './utils/utils'

const onStatusSuccess = () => {
  toast('Your rule was updated successfully', 'success')
}

const FILTER_ICON_CLASS = 'h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500'

const ACTION_FILTER_OPTIONS = ACTIONS_KEYS.map((a) => ({
  label: a.label.replace(/\b\w/g, (c) => c.toUpperCase()),
  value: a.value,
  icon: ACTION_ICON_MAP[a.value]
    ? React.cloneElement(ACTION_ICON_MAP[a.value] as React.ReactElement, {
        className: FILTER_ICON_CLASS,
      })
    : null,
}))

const getActionTooltipLabel = (action): string => {
  const baseLabel =
    ACTIONS_KEYS.find((a) => a.value === action.actionType)?.label ||
    formatSnakeValue(action.actionType)

  const templateName =
    action.textTemplate?.title ||
    action.emailTemplate?.title ||
    action.documentTemplatePackage?.packageName ||
    action.taskTemplate?.title ||
    action.noteTemplate?.title ||
    action.training?.name ||
    action.course?.name ||
    action.package?.name ||
    null

  return templateName ? `${baseLabel}: ${templateName}` : baseLabel
}

const CONDITION_KEY_ABBREV: Record<string, string> = {
  jobListingType: 'Listing Type',
  callCount: 'Calls',
  missedOutboundCallCount: 'Missed Calls',
  disqualifiedReason: 'Disqualified',
  notInterestedReason: 'Not Interested',
  terminatedReason: 'Terminated',
  jobListingId: 'Job Listing',
  tagId: 'Tag',
  linkType: 'Lead Source',
  eligibleForRehire: 'Rehire Eligible',
  [STEP_CONDITION_KEY]: 'Step',
}

const formatConditionValue = (
  condition: {
    key: string
    value: string
    stepType?: string
    stepStatus?: string
    customStep?: { label?: string | null } | null
    tag?: { label?: string | null; status?: string | null } | null
  },
  jobListingTitleById: Record<string, string>
): string => {
  const { key, value, stepType, stepStatus, customStep } = condition
  if (key === 'eligibleForRehire') {
    return value === 'true' ? 'Yes' : 'No'
  }
  if (key === STEP_CONDITION_KEY) {
    const stepLabel =
      customStep?.label ?? formatStepType(stepType as any) ?? stepType
    return `${stepLabel} — ${formatSnakeValue(stepStatus ?? '')}`
  }
  if (key === 'jobListingId') {
    return jobListingTitleById[value] || 'Deleted listing'
  }
  if (key === 'tagId') {
    return formatTagConditionLabel(condition.tag)
  }
  if (key === 'callCount') {
    const n = parseInt(value, 10)
    return isNaN(n) ? value : `${n} ${n === 1 ? 'call' : 'calls'}`
  }
  if (key === 'missedOutboundCallCount') {
    const n = parseInt(value, 10)
    return isNaN(n) ? value : `${n} missed ${n === 1 ? 'call' : 'calls'}`
  }
  return formatSnakeValue(value)
}

const RulesTab = () => {
  const [viewMode, setViewMode] = useState<'table' | 'pipeline'>('pipeline')
  const [selectedRule, setSelectedRule] = useState(null)
  const [opened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false)
  const [editing, setEditing] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false)
  const [rulePendingDelete, setRulePendingDelete] = useState(null)
  const [currentPageInfo, setCurrentPageInfo] = useState({
    limit: TABLE_PAGE_LIMIT,
    offset: 0,
  })
  const currentPageInfoRef = useRef(currentPageInfo)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchTerm, 300)
  const [actionTypeFilter, setActionTypeFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])

  const STATUS_FILTER_OPTIONS = [
    { label: 'Active', value: AutomatedRuleStatus.ACTIVE },
    { label: 'Inactive', value: AutomatedRuleStatus.INACTIVE },
  ]

  const filters = useMemo(() => {
    const f: {
      searchTerm?: string
      actionTypes?: string[]
      statuses?: string[]
    } = {}
    if (debouncedSearch) f.searchTerm = debouncedSearch
    if (actionTypeFilter.length) f.actionTypes = actionTypeFilter
    if (statusFilter.length) f.statuses = statusFilter
    return f
  }, [debouncedSearch, actionTypeFilter, statusFilter])

  // Pipeline view only ever shows ACTIVE rules — the live state of the
  // automation pipeline. Override the user's status filter in pipeline mode
  // so the query never returns inactive rules. We layer this on top of
  // `filters` (instead of inside the memo) so toggling viewMode doesn't churn
  // the `filters` reference and double-fire the filter-change effect.
  const queryFilters = useMemo(() => {
    if (viewMode === 'pipeline') {
      return { ...filters, statuses: [AutomatedRuleStatus.ACTIVE] }
    }
    return filters
  }, [filters, viewMode])

  const { data: jobListingsResponse } = useQuery(GET_JOB_LISTINGS, {
    variables: {
      pageInfo: {
        limit: 100,
        offset: 0,
      },
    },
  })

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  // Match the starting viewMode so the first fetch returns the right page
  // size. Subsequent viewMode/filter/pagination changes go through manual
  // refetch paths, so this value is only consulted on the initial query.
  const [initialPageInfo] = useState(() =>
    resolveRulesPageInfo(viewMode, { limit: TABLE_PAGE_LIMIT, offset: 0 })
  )

  const {
    data: automatedRulesList,
    loading: automatedRulesLoading,
    networkStatus,
    refetch,
  } = useQuery(GET_AUTOMATED_RULES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      pageInfo: initialPageInfo,
      filters: queryFilters,
    },
    onError,
  })

  // viewMode lives in state but every refetch site needs the *current* value.
  // A ref keeps the page-size logic correct in mutation onCompleted callbacks
  // and refetchQueries variables, which would otherwise close over stale state.
  const viewModeRef = useRef(viewMode)
  viewModeRef.current = viewMode

  // Single source of truth for "what limit/offset should this refetch use?"
  // Pipeline mode always pulls everything; table mode honors the user's
  // current pagination.
  const getEffectivePageInfo = useCallback(
    () => resolveRulesPageInfo(viewModeRef.current, currentPageInfoRef.current),
    []
  )

  const [setRuleStatus] = useMutation(SET_AUTOMATED_RULE_STATUS, {
    onCompleted: () => {
      onStatusSuccess()
      refetch({
        pageInfo: getEffectivePageInfo(),
        filters: queryFilters,
      })
    },
    onError,
  })

  const [deleteRule] = useMutation(DELETE_AUTOMATED_RULE, {
    onCompleted: () => {
      toast('Rule deleted successfully', 'success')
      refetch({
        pageInfo: getEffectivePageInfo(),
        filters: queryFilters,
      })
    },
    onError,
  })

  const onEdit = useCallback(
    (element) => {
      setSelectedRule(element)
      setEditing(true)
      setDuplicating(false)
      openDrawer()
    },
    [openDrawer]
  )

  const onCreate = useCallback(() => {
    setSelectedRule('create')
    setEditing(false)
    setDuplicating(false)
    openDrawer()
  }, [openDrawer])

  // Duplicate reuses the source rule's id to prefill the drawer (so it fetches
  // the full rule), but runs in create mode (isEditing=false) so saving inserts
  // a brand-new rule instead of overwriting the original.
  const onDuplicate = useCallback(
    (element) => {
      setSelectedRule(element)
      setEditing(false)
      setDuplicating(true)
      openDrawer()
    },
    [openDrawer]
  )

  const onStatusChange = useCallback(
    (rule, status) => {
      setRuleStatus({
        variables: {
          automatedRuleId: rule.automatedRuleId,
          status: status
            ? AutomatedRuleStatus.ACTIVE
            : AutomatedRuleStatus.INACTIVE,
        },
      })
    },
    [setRuleStatus]
  )

  const handleDelete = useCallback(
    (rule) => {
      setRulePendingDelete(rule)
      deleteModalHandlers.open()
    },
    [deleteModalHandlers]
  )

  const confirmDelete = useCallback(() => {
    if (!rulePendingDelete) return Promise.resolve()

    return deleteRule({
      variables: {
        automatedRuleId: rulePendingDelete.automatedRuleId,
      },
    }).finally(() => {
      setRulePendingDelete(null)
    })
  }, [deleteRule, rulePendingDelete])

  const handleLoadPage = useCallback(
    ({ offset, limit }) => {
      setCurrentPageInfo({ offset, limit })
      refetch({
        pageInfo: { offset, limit },
        filters: queryFilters,
      })
    },
    [refetch, queryFilters]
  )

  // Reset pagination to page 1 (table only) and refetch when filters change.
  // Pipeline mode keeps its full-set fetch; only table mode tracks page state.
  const [paginationResetKey, setPaginationResetKey] = useState(0)
  const filtersRef = useRef(filters)
  useEffect(() => {
    // Skip the initial render
    if (filtersRef.current === filters) return
    filtersRef.current = filters

    if (viewModeRef.current === 'table') {
      setCurrentPageInfo({ limit: TABLE_PAGE_LIMIT, offset: 0 })
      setPaginationResetKey((k) => k + 1)
    }
    refetch({
      pageInfo: resolveRulesPageInfo(viewModeRef.current, {
        limit: TABLE_PAGE_LIMIT,
        offset: 0,
      }),
      filters: queryFilters,
    })
  }, [filters, queryFilters, refetch])

  // When toggling between table and pipeline modes, refetch with the
  // appropriate page size. Pipeline needs all rules so every lane is
  // populated; table reverts to the user's current paginated view.
  const isInitialMountRef = useRef(true)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }
    refetch({
      pageInfo: getEffectivePageInfo(),
      filters: queryFilters,
    })
    // currentPageInfo and filters drive their own effects; this one only
    // fires on viewMode transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode])

  currentPageInfoRef.current = currentPageInfo

  const rules = automatedRulesList?.automatedRules?.items ?? []
  const totalCount = automatedRulesList?.automatedRules?.totalCount ?? 0
  const jobListings = jobListingsResponse?.jobListings?.items ?? []

  const jobListingTitleById = useMemo(() => {
    return jobListings.reduce<Record<string, string>>((acc, job) => {
      acc[job.jobListingId] = job.title
      return acc
    }, {})
  }, [jobListings])

  // The pipeline cards render condition values directly via ConditionChip,
  // which has no access to the jobListing title map. Resolve jobListingId
  // UUIDs to titles up here before handing rules to PipelineView, so the
  // chip shows "Acme Driver" instead of a UUID. Table view ignores this and
  // does its own resolution via formatConditionValue.
  const pipelineRules = useMemo(() => {
    if (viewMode !== 'pipeline') return rules
    return rules.map((rule) => ({
      ...rule,
      conditions: (rule.conditions ?? []).map((condition) => {
        if (condition.key === 'tagId') {
          return { ...condition, value: formatTagConditionLabel(condition.tag) }
        }
        if (condition.key !== 'jobListingId' || !condition.value) {
          return condition
        }
        return {
          ...condition,
          value: jobListingTitleById[condition.value] ?? 'Deleted listing',
        }
      }),
    }))
  }, [rules, viewMode, jobListingTitleById])

  const columns = useMemo(
    () => [
      {
        key: 'automatedRuleId',
        label: 'ID',
        width: 3,
        render: (rule) => (
          <div className="flex items-center gap-2">
            <Tooltip label={rule.automatedRuleId} withArrow>
              <ActionIcon
                variant="subtle"
                size={18}
                color="gray"
                onClick={(e) => {
                  e.stopPropagation()
                  navigator?.clipboard?.writeText?.(rule.automatedRuleId)
                }}
              >
                <IconCopy className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
              </ActionIcon>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'name',
        label: 'Rule Name',
        width: 28,
        render: (rule) => (
          <Tooltip label={rule.name} withArrow>
            <span className="truncate text-xs font-medium text-doubleNickel-gray-700">
              {rule.name}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 8,
        render: (rule) => {
          if (!rule.actions?.length) {
            return <span className="text-xs text-doubleNickel-gray-400">—</span>
          }

          // Group actions by type, preserving sort order
          const grouped = new Map<string, typeof rule.actions>()
          const sortedActions = [...rule.actions].sort(
            (a, b) =>
              (ACTION_SORT_ORDER[a.actionType] ?? 99) -
              (ACTION_SORT_ORDER[b.actionType] ?? 99)
          )
          sortedActions.forEach((action) => {
            const existing = grouped.get(action.actionType)
            if (existing) {
              existing.push(action)
            } else {
              grouped.set(action.actionType, [action])
            }
          })

          return (
            <div className="inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-2 py-1">
              {Array.from(grouped.entries()).map(([actionType, actions]) => {
                const tooltipLines = actions.map(getActionTooltipLabel)
                const count = actions.length

                return (
                  <Tooltip
                    key={actionType}
                    label={
                      tooltipLines.length > 1 ? (
                        <div className="flex flex-col gap-0.5 text-xs">
                          {tooltipLines.map((line, i) => (
                            <span key={i}>{line}</span>
                          ))}
                        </div>
                      ) : (
                        tooltipLines[0]
                      )
                    }
                    withArrow
                    multiline
                    maw={400}
                    withinPortal
                  >
                    <div className="relative flex items-center justify-center">
                      {ACTION_ICON_MAP[actionType] || (
                        <IconChecklist className={ACTION_ICON_CLASS} />
                      )}
                      {count > 1 && (
                        <span className="absolute -right-2 -top-2.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-doubleNickel-brand-500 text-[9px] font-bold text-white">
                          {count}
                        </span>
                      )}
                    </div>
                  </Tooltip>
                )
              })}
            </div>
          )
        },
      },
      {
        key: 'conditions',
        label: 'Conditions',
        width: 30,
        render: (rule) => {
          const conditions = rule.conditions ?? []
          if (!conditions.length) {
            return <span className="text-xs text-doubleNickel-gray-400">—</span>
          }

          // Extract stage info
          const applicantStage = conditions.find(
            (c) => c.key === 'applicantStage'
          )
          const hiringStage = conditions.find((c) => c.key === 'hiringStage')
          const extras = conditions.filter(
            (c) => c.key !== 'applicantStage' && c.key !== 'hiringStage'
          )

          // Build stage badge text
          const stageText = applicantStage
            ? formatSnakeValue(applicantStage.value)
            : null
          const hiringText = hiringStage
            ? formatSnakeValue(hiringStage.value)
            : null

          const badgeText =
            stageText && hiringText
              ? `${stageText} → ${hiringText}`
              : stageText || hiringText || '—'

          const isHired = applicantStage?.value === 'HIRED'

          // Group extras by key (same key can have multiple values)
          const extrasGrouped = new Map<string, string[]>()
          extras.forEach((c) => {
            const existing = extrasGrouped.get(c.key)
            const val = formatConditionValue(c, jobListingTitleById)
            if (existing) {
              existing.push(val)
            } else {
              extrasGrouped.set(c.key, [val])
            }
          })

          // Build display parts: each group shows abbreviated key + values
          const extraEntries = Array.from(extrasGrouped.entries()).map(
            ([key, values]) => {
              const abbrev =
                CONDITION_KEY_ABBREV[key] ||
                CONDITIONS_KEYS.find((ck) => ck.value === key)?.label ||
                formatSnakeValue(key)
              const fullLabel =
                CONDITIONS_KEYS.find((ck) => ck.value === key)?.label ||
                formatSnakeValue(key)
              const displayValue =
                values.length === 1
                  ? values[0]
                  : `${values[0]} +${values.length - 1}`
              return {
                label: fullLabel,
                abbrev,
                values,
                displayValue,
                hasDeletedListing:
                  key === 'jobListingId' &&
                  values.some((v) => v === 'Deleted listing'),
              }
            }
          )

          // Inline summary: show abbreviated key + value for each group
          const inlineParts = extraEntries.map(
            (e) => `${e.abbrev}: ${e.displayValue}`
          )

          // Full tooltip covering ALL conditions (stage + extras)
          const allConditionsTooltip = [
            `Stage: ${badgeText}`,
            ...extraEntries.map((e) => `${e.label}: ${e.values.join(', ')}`),
          ].join('\n')

          return (
            <Tooltip
              label={allConditionsTooltip}
              withArrow
              multiline
              maw={400}
              withinPortal
              style={{ whiteSpace: 'pre-line' }}
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    isHired
                      ? 'border border-green-200 bg-green-50 text-green-700'
                      : 'border border-blue-200 bg-blue-50 text-blue-700'
                  }`}
                >
                  {badgeText}
                </span>
                {inlineParts.length > 0 && (
                  <span className="text-xs leading-snug text-doubleNickel-gray-500">
                    {inlineParts.map((part, i) => {
                      const hasDeleted = extraEntries[i]?.hasDeletedListing
                      return (
                        <span key={i}>
                          {i > 0 ? ' · ' : '· '}
                          {hasDeleted ? (
                            <span className="italic text-doubleNickel-error-500">
                              {part}
                            </span>
                          ) : (
                            part
                          )}
                        </span>
                      )
                    })}
                  </span>
                )}
              </div>
            </Tooltip>
          )
        },
      },
      {
        key: 'createdBy',
        label: 'Created By',
        width: 7,
        render: (rule) => {
          if (!rule.createdBy) {
            return <span className="text-xs text-doubleNickel-gray-400">—</span>
          }
          return (
            <Tooltip label={buildFullName(rule.createdBy)} withArrow>
              <Avatar color="blue" size="sm" radius="sm">
                {rule.createdBy.firstName?.charAt(0)}
                {rule.createdBy.lastName?.charAt(0)}
              </Avatar>
            </Tooltip>
          )
        },
      },
      {
        key: 'updatedAt',
        label: 'Updated',
        width: 8,
        render: (rule) => {
          if (!rule.updatedAt) {
            return <span className="text-xs text-doubleNickel-gray-400">—</span>
          }
          return (
            <Tooltip label={formatDateTimeForTooltip(rule.updatedAt)} withArrow>
              <span className="text-xs text-doubleNickel-gray-500">
                {formatDateAbsolute(rule.updatedAt)}
              </span>
            </Tooltip>
          )
        },
      },
      {
        key: 'menu',
        label: '',
        width: 6,
        render: (rule) => (
          <div className="flex items-center justify-end gap-2">
            <Tooltip
              label={
                rule.status === AutomatedRuleStatus.ACTIVE
                  ? 'Disable automation'
                  : 'Enable automation'
              }
              withArrow
            >
              <div onClick={(e) => e.stopPropagation()}>
                <Switch
                  key={`${rule.automatedRuleId}-${rule.status}`}
                  size="xs"
                  color="#1C93D7"
                  defaultChecked={rule.status === AutomatedRuleStatus.ACTIVE}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(event) => {
                    event.stopPropagation()
                    const isActive = event.currentTarget.checked
                    onStatusChange(rule, isActive)
                  }}
                />
              </div>
            </Tooltip>
            <Menu shadow="md" position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <IconThreeDots className="h-4 w-4 fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-36">
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  leftSection={
                    <IconCopy className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    onDuplicate(rule)
                  }}
                >
                  Duplicate
                </Menu.Item>
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  leftSection={
                    <IconTrash className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDelete(rule)
                  }}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
      },
    ],
    [jobListingTitleById, handleDelete, onDuplicate, onStatusChange]
  )

  const handleRowClick = useCallback(
    (rowIndex: number) => {
      const rule = rules[rowIndex]
      if (rule) {
        onEdit(rule)
      }
    },
    [onEdit, rules]
  )

  const tableLoading =
    automatedRulesLoading || networkStatus === NetworkStatus.refetch

  return (
    <div className="h-full">
      <div className="flex h-full flex-col gap-4">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col">
            <div className="text-lg font-semibold text-doubleNickel-gray-900">
              Rules
            </div>
            <div className="text-sm text-doubleNickel-gray-600">
              <span>{totalCount}</span>
              <span> rules</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SegmentedControl
              size="xs"
              value={viewMode}
              onChange={(v) => setViewMode(v as 'table' | 'pipeline')}
              data={[
                { label: 'Table', value: 'table' },
                { label: 'Pipeline', value: 'pipeline' },
              ]}
            />
            <TextInput
              placeholder="Search by rule name..."
              leftSection={
                <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-400" />
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              className="w-64"
              size="sm"
            />
            <FilterMenu
              filters={[
                {
                  title: 'Action Type',
                  selected: actionTypeFilter,
                  onChange: setActionTypeFilter,
                  options: ACTION_FILTER_OPTIONS,
                },
                {
                  title: 'Status',
                  selected: statusFilter,
                  onChange: setStatusFilter,
                  options: STATUS_FILTER_OPTIONS,
                },
              ]}
            />
            <DNButton variant="primary" onClick={onCreate}>
              Create Rule
            </DNButton>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          {viewMode === 'pipeline' ? (
            <PipelineView rules={pipelineRules} onRuleClick={onEdit} />
          ) : (
            <DnTable
              key={paginationResetKey}
              columns={columns}
              data={rules}
              enableColumnResize={false}
              loading={tableLoading}
              totalCount={totalCount}
              onLoadPage={handleLoadPage}
              defaultResultsPerPage={TABLE_PAGE_LIMIT}
              onRowClick={handleRowClick}
              emptyStateMessage="No rules found. Add one to get started."
            />
          )}
        </div>
      </div>
      {selectedRule && (
        <RuleDetailsPage
          opened={opened}
          close={closeDrawer}
          setSelectedRule={setSelectedRule}
          selectedRuleId={selectedRule?.automatedRuleId}
          isEditing={editing}
          isDuplicating={duplicating}
          refetchQueries={[
            {
              query: GET_AUTOMATED_RULES,
              variables: {
                pageInfo: getEffectivePageInfo(),
                filters: queryFilters,
              },
            },
          ]}
        />
      )}
      {deleteModalOpened && (
        <DeleteModal
          title={`Delete ${rulePendingDelete?.name ?? 'rule'}?`}
          opened={deleteModalOpened}
          close={() => {
            deleteModalHandlers.close()
            setRulePendingDelete(null)
          }}
          onDelete={confirmDelete}
        />
      )}
    </div>
  )
}

export default RulesTab
